import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "use-intl";

import { api } from "@/api/api";
import type {
  ValidatePackagePromoCodeInputDto,
  ValidatePromoCodeOutputDto,
  ValidateSessionPromoCodeInputDto,
} from "@/types/codegen";
import { formatCurrency } from "@/utils/currency";
import { Feature, isFeatureEnabled } from "@/utils/featureFlags";

export type ValidationStatus = {
  type: "success" | "error" | "loading";
  message?: string;
};

type ParamsMap = {
  trips: ValidatePackagePromoCodeInputDto;
  flights: ValidateSessionPromoCodeInputDto;
  hotels: ValidateSessionPromoCodeInputDto;
};

const serviceToUrlMap: Record<keyof ParamsMap, string> = {
  trips: "/packages/promo-code/validate",
  flights: "/flights/promo-code/validate",
  hotels: "/hotels/promo-code/validate",
};

type Params<T extends keyof ParamsMap> = {
  service: T;
  validationRequestData: ParamsMap[T];
};

export function usePromoCodeValidation<T extends keyof ParamsMap>({
  service,
  validationRequestData,
}: Params<T>) {
  const t = useTranslations();

  const queryResult = useQuery({
    queryKey: [
      service,
      "promo-code-validation",
      validationRequestData,
    ] as const,
    queryFn: ({ signal, queryKey: [, , payload] }) => {
      return api.post<ValidatePromoCodeOutputDto>(
        serviceToUrlMap[service],
        payload,
        { signal },
      );
    },
    select: (response) => response.data.data,
    enabled:
      Boolean(validationRequestData.code) &&
      isFeatureEnabled(Feature.PromoCodes),
    refetchOnMount: true,
    staleTime: 0,
  });

  const { data, isError, isFetching, refetch } = queryResult;

  const validationStatus: ValidationStatus | undefined = isFetching
    ? { type: "loading" }
    : isError
      ? { type: "error", message: t("promoCode.failedToApply") }
      : data?.isValid === false
        ? { type: "error", message: data.message || t("promoCode.invalid") }
        : data?.isValid === true
          ? {
              type: "success",
              message: t("promoCode.appliedSuccessfully", {
                amount: formatCurrency(data.discount),
              }),
            }
          : undefined;

  return {
    validationStatus,
    data,
    revalidate: refetch,
  };
}
